import { useEffect, type CSSProperties, type ReactNode } from "react";
import "./ui.css";

// Web ports of the shared SwiftUI primitives (SBCard, PrimaryActionButton,
// SportsBackground, LoadingView) from Views/Components/SharedUIComponents.swift.

export function SBCard({ children, className, style }: { children: ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <div className={`sb-card${className ? ` ${className}` : ""}`} style={style}>
      {children}
    </div>
  );
}

export function SportsBackground({ children }: { children?: ReactNode }) {
  return (
    <div className="sports-background">
      <div className="sports-background-glow" aria-hidden />
      {children}
    </div>
  );
}

export function PrimaryButton({
  title,
  onClick,
  disabled,
  type = "button",
}: {
  title: string;
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit";
}) {
  return (
    <button className="sb-button-primary" type={type} onClick={onClick} disabled={disabled}>
      {title}
    </button>
  );
}

export function SecondaryButton({
  title,
  onClick,
  disabled,
  destructive,
}: {
  title: string;
  onClick?: () => void;
  disabled?: boolean;
  destructive?: boolean;
}) {
  return (
    <button
      className={`sb-button-secondary${destructive ? " destructive" : ""}`}
      type="button"
      onClick={onClick}
      disabled={disabled}
    >
      {title}
    </button>
  );
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="sb-segmented" role="tablist">
      {options.map((option) => (
        <button
          key={option.value}
          role="tab"
          type="button"
          aria-selected={option.value === value}
          className={option.value === value ? "selected" : ""}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function LoadingView({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="sb-loading" role="status">
      <div className="sb-spinner" aria-hidden />
      <span>{label}</span>
    </div>
  );
}

export function ErrorText({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <p className="sb-error-text">{children}</p>;
}

/**
 * Two-step confirm for destructive actions (web stand-in for the iOS
 * confirmationDialog + alert pair). Render it unconditionally; it paints
 * nothing while `open` is false.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmTitle,
  destructive,
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmTitle: string;
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onCancel]);

  if (!open) return null;
  return (
    <div className="sb-dialog-overlay" onClick={() => !busy && onCancel()}>
      <div
        className="sb-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sb-dialog-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="sb-dialog-title">{title}</h2>
        <p>{message}</p>
        <div className="sb-dialog-actions">
          <SecondaryButton title="Cancel" onClick={onCancel} disabled={busy} />
          <SecondaryButton title={busy ? "Working…" : confirmTitle} destructive={destructive} onClick={onConfirm} disabled={busy} />
        </div>
      </div>
    </div>
  );
}

export function FieldLabel({ label, className, children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <label className={className ? `sb-field ${className}` : "sb-field"}>
      <span className="sb-field-label">{label}</span>
      {children}
    </label>
  );
}

/**
 * FieldLabel's look without the <label> semantics. Use it around lists of
 * buttons / drag rows: a <label> forwards clicks on non-interactive content to
 * its first labelable descendant, which would press the first row's button.
 */
export function FieldGroup({ label, className, children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <div role="group" aria-label={label} className={className ? `sb-field ${className}` : "sb-field"}>
      <span className="sb-field-label">{label}</span>
      {children}
    </div>
  );
}
